import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeftIcon,
  EyeIcon,
  PaperAirplaneIcon,
  PlayIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import WhatsAppDevicePreview from './WhatsAppDevicePreview';
import WhatsAppRecipientSelectModal, {
  type RecipientSourceTab,
} from './WhatsAppRecipientSelectModal';
import WhatsAppTemplateMappingsSection from './WhatsAppTemplateMappingsSection';
import WhatsAppCampaignPolicySection from './WhatsAppCampaignPolicySection';
import WhatsAppTemplateSearchSelect from './WhatsAppTemplateSearchSelect';
import WhatsAppCampaignSendPreflightDialog, {
  type WeeklyCapChoice,
} from './WhatsAppCampaignSendPreflightDialog';
import {
  cloneTemplateMappings,
  emptyMappingsForVariableCount,
  templateHasEditableVariables,
} from './whatsappCampaignMappingUtils';
import { parseConsecutiveTemplateSlots } from './whatsappTemplateSlots';
import WhatsAppMediaHeaderUploader from './WhatsAppMediaHeaderUploader';
import {
  buildPreviewMessage,
  isMediaHeaderFormat,
} from './whatsappPreview';
import { devicePreviewFromTemplate, templateHeaderMimeType } from './whatsappTemplatePreview';
import {
  bodyValuesFromMappings,
  buildCampaignPayload,
  campaignToForm,
  emptyCampaignForm,
  inputClass,
  isPickerAudience,
  isSingleAudience,
  previewBodyValuesFromMappings,
  recipientsToPhoneLines,
  validateCampaignForm,
  type CampaignFormState,
} from './whatsappCampaignFormUtils';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';
import { WHATSAPP_BASE_PATH, type WhatsAppSendRecipient } from '../../models/MasterAdmin/whatsappHub';
import type {
  WhatsAppCampaign,
  WhatsAppCampaignMeta,
  WhatsAppRecipientPreview,
  WhatsAppServiceVertical,
  WhatsAppTargetAudience,
  WhatsAppTemplate,
} from '../../models/MasterAdmin/whatsappCampaign';
import { AUDIENCE_LABELS } from '../../models/MasterAdmin/whatsappCampaign';
import {
  activateWhatsAppCampaign,
  createWhatsAppCampaign,
  estimateWhatsAppRecipients,
  fetchWhatsAppAudiences,
  fetchApprovedWhatsAppTemplates,
  getWhatsAppCampaign,
  previewWhatsAppCampaign,
  preflightWhatsAppCampaignSend,
  sendWhatsAppCampaign,
  updateWhatsAppCampaign,
  type WhatsAppCampaignSendPreflight,
} from '../../services/MasterAdmin/whatsappCampaignService';
import { formatDisplayPhone } from './whatsappUi';

function headerMediaFromTemplate(tpl: WhatsAppTemplate | undefined) {
  if (!tpl || !isMediaHeaderFormat(tpl.headerFormat)) return null;
  const url = String(tpl.headerHandleFileUrl || '').trim();
  if (!url) return null;
  return {
    headerMediaUrl: url,
    headerMimeType: templateHeaderMimeType(tpl.headerFormat) || '',
    headerFileName: String(tpl.headerHandleFileName || '').trim(),
  };
}

function allowedSourcesForAudience(audience: WhatsAppTargetAudience): RecipientSourceTab[] {
  if (audience === 'SINGLE_USER') return ['users', 'contacts'];
  if (audience === 'SINGLE_DOCTOR') return ['vendors'];
  if (audience === 'SINGLE_VENDOR') return ['vendors', 'contacts'];
  return ['contacts', 'users', 'vendors'];
}

const WhatsAppCampaignEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const { campaignId } = useParams<{ campaignId?: string }>();
  const isEdit = Boolean(campaignId);

  const [meta, setMeta] = useState<WhatsAppCampaignMeta | null>(null);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<CampaignFormState>(emptyCampaignForm());
  const [estimatedCount, setEstimatedCount] = useState<number | null>(null);
  const [selectedRecipients, setSelectedRecipients] = useState<WhatsAppSendRecipient[]>([]);
  const [allowMultiSelect, setAllowMultiSelect] = useState(false);
  const [recipientModalOpen, setRecipientModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewTotal, setPreviewTotal] = useState(0);
  const [previewParticipants, setPreviewParticipants] = useState<WhatsAppRecipientPreview[]>([]);
  const [preflightOpen, setPreflightOpen] = useState(false);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [preflightData, setPreflightData] = useState<WhatsAppCampaignSendPreflight | null>(null);
  const [pendingSendCampaignId, setPendingSendCampaignId] = useState<string | null>(null);
  const [messagePreviewOpen, setMessagePreviewOpen] = useState(false);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.name === form.templateName),
    [templates, form.templateName]
  );

  const COST_PER_MSG_INR: Record<string, number> = {
    MARKETING: 0.784,
    UTILITY: 0.115,
    AUTHENTICATION: 0.115,
    SERVICE: 0.115,
  };

  const audienceCountForCost = useMemo(() => {
    if (estimatedCount != null) return estimatedCount;
    if (selectedRecipients.length > 0) return selectedRecipients.length;
    if (form.customPhonesText?.trim()) {
      return form.customPhonesText
        .split(/[\n,;]+/)
        .map((s) => s.trim())
        .filter(Boolean).length;
    }
    return null;
  }, [estimatedCount, selectedRecipients.length, form.customPhonesText]);

  const estimatedCampaignCost = useMemo(() => {
    if (audienceCountForCost == null || audienceCountForCost <= 0) return null;
    const category = String(
      form.messageCategory || selectedTemplate?.category || 'MARKETING'
    ).toUpperCase();
    const rate = COST_PER_MSG_INR[category] ?? COST_PER_MSG_INR.MARKETING;
    return {
      contacts: audienceCountForCost,
      category,
      rate,
      projectedCostInr: Math.round(audienceCountForCost * rate * 100) / 100,
    };
  }, [audienceCountForCost, form.messageCategory, selectedTemplate?.category]);

  const hasEditableVariables = templateHasEditableVariables(
    selectedTemplate?.bodyPreview,
    selectedTemplate?.variableCount
  );
  const templateDefaultMappings = selectedTemplate?.defaultVariableMappings || [];

  const previewBodyValues = useMemo(
    () =>
      previewBodyValuesFromMappings(
        form.templateVariableMappings,
        form.bodyValues,
        selectedTemplate?.variableCount || 0
      ),
    [form.templateVariableMappings, form.bodyValues, selectedTemplate?.variableCount]
  );

  const previewMessage = useMemo(
    () => buildPreviewMessage(selectedTemplate?.bodyPreview || '', previewBodyValues),
    [selectedTemplate, previewBodyValues]
  );

  const devicePreviewProps = useMemo(() => {
    const includeOptOut = form.sendPolicy?.includeOptOut !== false && form.messageCategory === 'MARKETING';
    const stopButton = { type: 'QUICK_REPLY', text: 'Stop' };
    if (!selectedTemplate) {
      return {
        message: previewMessage || 'Select a template to preview',
        headerText: null as string | null,
        footer: includeOptOut ? 'Reply STOP to opt out' : (null as string | null),
        buttons: includeOptOut
          ? [stopButton]
          : ([] as import('../../models/MasterAdmin/whatsappCampaign').WhatsAppTemplateButton[]),
        headerMediaUrl: form.headerMediaUrl || null,
        headerMimeType: form.headerMimeType || null,
      };
    }
    const fromTpl = devicePreviewFromTemplate(selectedTemplate, {
      headerMediaUrl: form.headerMediaUrl || null,
      headerMimeType:
        form.headerMimeType ||
        templateHeaderMimeType(selectedTemplate.headerFormat) ||
        null,
    });
    const existingButtons = Array.isArray(fromTpl.buttons) ? [...fromTpl.buttons] : [];
    const hasStop = existingButtons.some((b) => String(b.text || '').toLowerCase() === 'stop');
    return {
      ...fromTpl,
      message: previewMessage,
      footer: fromTpl.footer || (includeOptOut ? 'Reply STOP to opt out' : fromTpl.footer),
      buttons: includeOptOut && !hasStop ? [...existingButtons, stopButton] : existingButtons,
    };
  }, [selectedTemplate, previewMessage, form.headerMediaUrl, form.headerMimeType, form.sendPolicy?.includeOptOut, form.messageCategory]);

  const serviceEvents = useMemo(() => {
    if (!form.serviceVertical || !meta?.intentEventsByService) return [];
    return meta.intentEventsByService[form.serviceVertical] || [];
  }, [form.serviceVertical, meta]);

  const loadMeta = useCallback(async () => {
    const [metaRes, tplRes] = await Promise.all([fetchWhatsAppAudiences(), fetchApprovedWhatsAppTemplates()]);
    setMeta(metaRes.data);
    setTemplates(tplRes.data || []);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        await loadMeta();
        if (campaignId) {
          const res = await getWhatsAppCampaign(campaignId);
          if (!cancelled) {
            const next = campaignToForm(res.data);
            setForm(next);
            if (next.targetUserId || next.targetVendorId) {
              setSelectedRecipients([
                {
                  key: next.targetUserId || next.targetVendorId,
                  countryCode: '+91',
                  phoneNumber: '',
                  displayName: next.targetDisplayName || 'Saved recipient',
                  maskedPhone: '',
                  recipientType: next.targetAudience === 'SINGLE_USER' ? 'user' : 'vendor',
                  recipientId: next.targetUserId || next.targetVendorId,
                },
              ]);
              setAllowMultiSelect(false);
            } else if (next.targetAudience === 'CUSTOM_PHONES') {
              setAllowMultiSelect(true);
            }
          }
        } else if (!cancelled) {
          setForm(emptyCampaignForm());
        }
      } catch (err: unknown) {
        if (!cancelled) toast.error(getApiErrorMessage(err, 'Failed to load campaign'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [campaignId, loadMeta]);

  useEffect(() => {
    if (!form.templateName || form.headerMediaUrl.trim()) return;
    const tpl = templates.find((t) => t.name === form.templateName);
    const media = headerMediaFromTemplate(tpl);
    if (!media) return;
    setForm((prev) => ({
      ...prev,
      headerMediaUrl: media.headerMediaUrl,
      headerMimeType: media.headerMimeType,
      headerFileName: media.headerFileName,
    }));
  }, [form.templateName, form.headerMediaUrl, templates]);

  useEffect(() => {
    if (!form.templateName || !templates.length) return;
    if (form.templateVariableMappings.length > 0) return;
    const tpl = templates.find((t) => t.name === form.templateName);
    if (!tpl || !templateHasEditableVariables(tpl.bodyPreview, tpl.variableCount)) return;
    const defaults = cloneTemplateMappings(tpl.defaultVariableMappings);
    const varCount = Math.max(
      tpl.variableCount ?? 0,
      defaults.length,
      parseConsecutiveTemplateSlots(tpl.bodyPreview || '').count
    );
    const mappings =
      defaults.length > 0 ? defaults : emptyMappingsForVariableCount(varCount, form.campaignType);
    setForm((prev) => ({
      ...prev,
      templateVariableMappings: mappings,
      bodyValues: bodyValuesFromMappings(mappings, prev.campaignType),
    }));
  }, [form.templateName, form.templateVariableMappings.length, form.campaignType, templates]);

  const applyTemplateSelection = (name: string) => {
    const tpl = templates.find((t) => t.name === name);
    const defaults = cloneTemplateMappings(tpl?.defaultVariableMappings);
    const varCount = Math.max(
      tpl?.variableCount ?? 0,
      defaults.length,
      parseConsecutiveTemplateSlots(tpl?.bodyPreview || '').count
    );
    const mappings =
      defaults.length > 0
        ? defaults
        : emptyMappingsForVariableCount(varCount, form.campaignType);
    const bodyValues = bodyValuesFromMappings(mappings, form.campaignType);
    const templateMedia = headerMediaFromTemplate(tpl);
    const needsMedia = tpl && isMediaHeaderFormat(tpl.headerFormat);

    setForm({
      ...form,
      templateName: name,
      templateLanguageCode: tpl?.languageCode || 'en',
      messageCategory:
        (tpl?.category as CampaignFormState['messageCategory']) || form.messageCategory,
      templateVariableMappings: mappings,
      bodyValues,
      headerMediaUrl: templateMedia?.headerMediaUrl || (needsMedia ? '' : form.headerMediaUrl),
      headerMimeType: templateMedia?.headerMimeType || (needsMedia ? '' : form.headerMimeType),
      headerFileName: templateMedia?.headerFileName || (needsMedia ? '' : form.headerFileName),
    });
  };

  const resetMappingsToTemplateDefaults = () => {
    if (!selectedTemplate) return;
    const defaults = cloneTemplateMappings(selectedTemplate.defaultVariableMappings);
    const varCount = Math.max(
      selectedTemplate.variableCount ?? 0,
      defaults.length,
      parseConsecutiveTemplateSlots(selectedTemplate.bodyPreview || '').count
    );
    const mappings =
      defaults.length > 0
        ? defaults
        : emptyMappingsForVariableCount(varCount, form.campaignType);
    setForm({
      ...form,
      templateVariableMappings: mappings,
      bodyValues: bodyValuesFromMappings(mappings, form.campaignType),
    });
    toast.success(
      defaults.length
        ? 'Reset to template variable mappings'
        : 'Cleared to empty editable mappings'
    );
  };

  const syncRecipientsIntoForm = (
    next: WhatsAppSendRecipient[],
    audienceOverride?: WhatsAppTargetAudience
  ) => {
    setSelectedRecipients(next);
    const audience = audienceOverride || form.targetAudience;

    if (audience === 'CUSTOM_PHONES' || next.length > 1) {
      setForm((prev) => ({
        ...prev,
        targetAudience: 'CUSTOM_PHONES',
        recipientMode: 'custom',
        targetUserId: '',
        targetVendorId: '',
        targetDisplayName: next.length === 1 ? next[0].displayName || '' : `${next.length} recipients`,
        targetPhone: '',
        customPhonesText: recipientsToPhoneLines(next),
      }));
      setAllowMultiSelect(true);
      return;
    }

    const r = next[0];
    if (!r) {
      setForm((prev) => ({
        ...prev,
        targetUserId: '',
        targetVendorId: '',
        targetDisplayName: '',
        targetPhone: '',
        customPhonesText: '',
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      targetUserId: audience === 'SINGLE_USER' ? r.recipientId || '' : '',
      targetVendorId:
        audience === 'SINGLE_VENDOR' || audience === 'SINGLE_DOCTOR' ? r.recipientId || '' : '',
      targetDisplayName: r.displayName || '',
      targetPhone: formatDisplayPhone(r.countryCode, r.phoneNumber),
      customPhonesText: '',
    }));
  };

  const handleAudienceChange = (audience: WhatsAppTargetAudience) => {
    setSelectedRecipients([]);
    setEstimatedCount(null);
    const multi = audience === 'CUSTOM_PHONES';
    setAllowMultiSelect(multi);
    setForm({
      ...form,
      targetAudience: audience,
      recipientMode: multi ? 'custom' : 'audience',
      targetUserId: '',
      targetVendorId: '',
      targetDisplayName: '',
      targetPhone: '',
      customPhonesText: multi ? form.customPhonesText : '',
    });
    if (isPickerAudience(audience)) {
      setRecipientModalOpen(true);
    } else {
      setRecipientModalOpen(false);
    }
  };

  const enableMultiSelectMode = () => {
    setAllowMultiSelect(true);
    const phones = recipientsToPhoneLines(selectedRecipients);
    setForm((prev) => ({
      ...prev,
      targetAudience: 'CUSTOM_PHONES',
      recipientMode: 'custom',
      targetUserId: '',
      targetVendorId: '',
      targetDisplayName:
        selectedRecipients.length > 1
          ? `${selectedRecipients.length} recipients`
          : selectedRecipients[0]?.displayName || '',
      targetPhone: '',
      customPhonesText: phones || prev.customPhonesText,
    }));
    setRecipientModalOpen(true);
  };

  const openFilteredSubsetPicker = () => {
    setAllowMultiSelect(true);
    setForm((prev) => ({
      ...prev,
      targetAudience: 'CUSTOM_PHONES',
      recipientMode: 'custom',
      targetUserId: '',
      targetVendorId: '',
      targetDisplayName: '',
      targetPhone: '',
      customPhonesText: '',
      filterCity: '',
      filterState: '',
    }));
    setSelectedRecipients([]);
    setRecipientModalOpen(true);
  };

  const handleRecipientModalConfirm = (next: WhatsAppSendRecipient[]) => {
    syncRecipientsIntoForm(
      next,
      allowMultiSelect || form.targetAudience === 'CUSTOM_PHONES' ? 'CUSTOM_PHONES' : undefined
    );
    setRecipientModalOpen(false);
  };

  const runPreflightAndSend = async (campaign: WhatsAppCampaign) => {
    if (form.messageCategory !== 'MARKETING' || form.scheduleMode === 'later') {
      await sendWhatsAppCampaign(campaign.id, {
        scheduleMode: form.scheduleMode,
        scheduledAt: form.scheduledAt || undefined,
        scheduleTimezone: 'Asia/Kolkata',
      });
      toast.success(form.scheduleMode === 'later' ? 'Campaign scheduled' : 'Campaign queued');
      navigate(`${WHATSAPP_BASE_PATH}/campaigns/${campaign.id}`);
      return;
    }
    setPreflightLoading(true);
    setPendingSendCampaignId(campaign.id);
    try {
      const res = await preflightWhatsAppCampaignSend(campaign.id);
      setPreflightData(res.data);
      setPreflightOpen(true);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Preflight failed'));
    } finally {
      setPreflightLoading(false);
    }
  };

  const confirmPreflightSend = async (choice: WeeklyCapChoice) => {
    if (!pendingSendCampaignId) return;
    setPreflightLoading(true);
    try {
      const res = await sendWhatsAppCampaign(pendingSendCampaignId, {
        scheduleMode: 'now',
        weeklyCapAction: choice === 'override' ? 'override' : choice,
        overrideWeeklyCap: choice === 'override',
        scheduleNextWeekAt: preflightData?.scheduleNextWeekAt,
      });
      toast.success(res.message || 'Campaign queued with policy');
      setPreflightOpen(false);
      navigate(`${WHATSAPP_BASE_PATH}/campaigns/${pendingSendCampaignId}`);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Send failed'));
    } finally {
      setPreflightLoading(false);
    }
  };

  const handleSave = async (andSend = false, andActivate = false) => {
    const error = validateCampaignForm(form);
    if (error) {
      toast.error(error);
      return;
    }
    if (
      form.targetAudience === 'SINGLE_USER' &&
      selectedRecipients[0] &&
      !selectedRecipients[0].recipientId
    ) {
      toast.error('Selected contact is not linked to a user. Pick from Users tab or link the contact.');
      return;
    }
    if (
      (form.targetAudience === 'SINGLE_VENDOR' || form.targetAudience === 'SINGLE_DOCTOR') &&
      selectedRecipients[0] &&
      !selectedRecipients[0].recipientId
    ) {
      toast.error('Selected contact is not linked to a vendor. Pick from Vendors tab.');
      return;
    }
    if (form.targetAudience === 'CUSTOM_PHONES') {
      const phones = form.customPhonesText
        .split(/[\n,]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      if (!phones.length && !selectedRecipients.length) {
        toast.error('Select at least one recipient or paste phone numbers');
        return;
      }
      if (selectedRecipients.length && !phones.length) {
        // Keep phones in sync before save
        setForm((prev) => ({ ...prev, customPhonesText: recipientsToPhoneLines(selectedRecipients) }));
      }
    }

    setSaving(true);
    try {
      const formForSave: CampaignFormState = {
        ...form,
        customPhonesText:
          form.targetAudience === 'CUSTOM_PHONES' && selectedRecipients.length
            ? recipientsToPhoneLines(selectedRecipients) || form.customPhonesText
            : form.customPhonesText,
      };
      const payload = buildCampaignPayload(formForSave, serviceEvents);
      let campaign: WhatsAppCampaign;
      if (isEdit && campaignId) {
        const res = await updateWhatsAppCampaign(campaignId, payload);
        campaign = res.data;
      } else {
        const res = await createWhatsAppCampaign(payload);
        campaign = res.data;
      }

      if (andActivate && campaign.campaignType === 'AUTOMATED') {
        await activateWhatsAppCampaign(campaign.id);
        toast.success('Automated campaign saved and activated');
      } else if (andSend && campaign.campaignType === 'BROADCAST') {
        await runPreflightAndSend(campaign);
        return;
      } else {
        toast.success(isEdit ? 'Campaign updated' : 'Campaign created');
      }

      navigate(`${WHATSAPP_BASE_PATH}/campaigns`);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleEstimate = async () => {
    const error = validateCampaignForm(form);
    if (error) {
      toast.error(error);
      return;
    }
    try {
      const res = await estimateWhatsAppRecipients(buildCampaignPayload(form, serviceEvents));
      setEstimatedCount(res.data.estimatedRecipients);
    } catch {
      toast.error('Estimate failed');
    }
  };

  const handleOpenPreview = async () => {
    const error = validateCampaignForm(form);
    if (error) {
      toast.error(error);
      return;
    }
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const res = await previewWhatsAppCampaign({
        ...buildCampaignPayload(form, serviceEvents),
        previewLimit: 50,
      });
      setPreviewTotal(res.data.total || 0);
      setPreviewParticipants(res.data.participants || []);
      setEstimatedCount(res.data.total ?? null);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Preview failed'));
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center p-8 text-sm text-gray-500">Loading…</div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="shrink-0 border-b border-gray-200 bg-white px-4 py-4 dark:border-gray-700 dark:bg-gray-900 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(`${WHATSAPP_BASE_PATH}/campaigns`)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Back
            </button>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {isEdit ? 'Edit campaign' : 'New campaign'}
              </h2>
              <p className="text-sm text-gray-500">
                {form.campaignType === 'AUTOMATED'
                  ? 'Automated retargeting — ML enqueues send jobs'
                  : 'One-time broadcast to your audience'}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setMessagePreviewOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
            >
              <EyeIcon className="h-4 w-4" />
              Preview message
            </button>
            {form.campaignType === 'BROADCAST' && (
              <button
                type="button"
                disabled={saving || previewLoading}
                onClick={() => void handleOpenPreview()}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
              >
                <EyeIcon className="h-4 w-4" />
                Preview participants
              </button>
            )}
            <button
              type="button"
              disabled={saving}
              onClick={() => void handleSave(false)}
              className="rounded-lg border border-[#25D366] px-4 py-2 text-sm font-medium text-[#25D366] disabled:opacity-60"
            >
              Save draft
            </button>
            {form.campaignType === 'BROADCAST' ? (
              <button
                type="button"
                disabled={saving}
                onClick={() => void handleSave(true)}
                className="inline-flex items-center gap-2 rounded-lg bg-[#25D366] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              >
                <PaperAirplaneIcon className="h-4 w-4" />
                {form.scheduleMode === 'later' ? 'Save & schedule' : 'Save & send'}
              </button>
            ) : (
              <button
                type="button"
                disabled={saving}
                onClick={() => void handleSave(false, true)}
                className="inline-flex items-center gap-2 rounded-lg bg-[#25D366] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              >
                <PlayIcon className="h-4 w-4" />
                Save & activate
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <div className="min-w-0 flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="mx-auto max-w-3xl space-y-4">
            <input
              placeholder="Campaign name *"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={inputClass}
            />
            {estimatedCampaignCost && (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[#25D366]/30 bg-[#25D366]/5 px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    Estimated cost: ₹{estimatedCampaignCost.projectedCostInr.toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500">
                    ~{estimatedCampaignCost.contacts.toLocaleString()} contacts × ₹
                    {estimatedCampaignCost.rate.toFixed(3)} / msg ({estimatedCampaignCost.category}) — billed on
                    delivery, same as Interakt
                  </p>
                </div>
                <button type="button" onClick={() => void handleEstimate()} className="text-xs font-medium text-[#128C7E] underline">
                  Recalculate audience
                </button>
              </div>
            )}

            {form.campaignType === 'AUTOMATED' && (
              <div className="rounded-xl border border-[#25D366]/30 bg-[#25D366]/5 p-4 dark:border-[#25D366]/20">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Automation rules</h4>
                <p className="mt-1 text-xs text-gray-500">
                  With analysis source <strong>Rules engine</strong>, app analytics events enqueue WhatsApp
                  jobs automatically. Template variable mappings fill patient/doctor fields at send time.
                  Activate the campaign to go live.
                </p>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs text-gray-500">Service vertical</label>
                    <select
                      className={inputClass}
                      value={form.serviceVertical}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          serviceVertical: e.target.value as WhatsAppServiceVertical,
                          ruleConfig: {
                            ...form.ruleConfig,
                            intentEvents: meta?.intentEventsByService?.[e.target.value] || [],
                          },
                        })
                      }
                    >
                      {(meta?.serviceVerticals || []).map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-gray-500">Analysis source</label>
                    <select
                      className={inputClass}
                      value={form.analysisSource}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          analysisSource: e.target.value as CampaignFormState['analysisSource'],
                        })
                      }
                    >
                      <option value="RULES">Rules engine (event-driven)</option>
                      <option value="AI_BACKEND">AI backend (ML worker)</option>
                      <option value="MANUAL">Manual only</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-gray-500">Delay (minutes)</label>
                    <input
                      type="number"
                      min={1}
                      max={120}
                      className={inputClass}
                      value={form.ruleConfig.delayMinutes ?? 15}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          ruleConfig: { ...form.ruleConfig, delayMinutes: Number(e.target.value) },
                        })
                      }
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-gray-500">Min intent score</label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      className={inputClass}
                      value={form.ruleConfig.minIntentScore ?? 60}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          ruleConfig: { ...form.ruleConfig, minIntentScore: Number(e.target.value) },
                        })
                      }
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-gray-500">Cooldown (hours)</label>
                    <input
                      type="number"
                      min={1}
                      max={72}
                      className={inputClass}
                      value={form.ruleConfig.cooldownHours ?? 24}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          ruleConfig: { ...form.ruleConfig, cooldownHours: Number(e.target.value) },
                        })
                      }
                    />
                  </div>
                </div>
                {serviceEvents.length > 0 && (
                  <p className="mt-2 text-xs text-gray-500">Trigger events: {serviceEvents.join(', ')}</p>
                )}
                <div className="mt-3">
                  <label className="mb-1 block text-xs text-gray-500">ML analysis config (JSON)</label>
                  <textarea
                    rows={3}
                    className={`${inputClass} font-mono text-xs`}
                    value={form.analysisConfigJson}
                    onChange={(e) => setForm({ ...form, analysisConfigJson: e.target.value })}
                    placeholder='{"modelVersion":"v1","campaignKey":"doctor_nudge"}'
                  />
                </div>
              </div>
            )}

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500">Template *</label>
              <WhatsAppTemplateSearchSelect
                templates={templates}
                value={form.templateName}
                onChange={applyTemplateSelection}
                placeholder="Search template name…"
                emptyLabel="Select approved template *"
              />
              {form.messageCategory === 'MARKETING' && (
                <label className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={form.sendPolicy.includeOptOut !== false}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        sendPolicy: { ...form.sendPolicy, includeOptOut: e.target.checked },
                      })
                    }
                  />
                  <span>
                    <strong>Add STOP opt-out</strong> — footer “Reply STOP to opt out” and a Stop quick-reply
                    (Meta marketing requirement). Must also exist on the Interakt/Meta template.
                  </span>
                </label>
              )}
            </div>

            {selectedTemplate && hasEditableVariables && (
              <WhatsAppTemplateMappingsSection
                bodyPreview={selectedTemplate.bodyPreview || ''}
                mappings={form.templateVariableMappings}
                onMappingsChange={(templateVariableMappings) =>
                  setForm({
                    ...form,
                    templateVariableMappings,
                    bodyValues: bodyValuesFromMappings(templateVariableMappings, form.campaignType),
                  })
                }
                showSaveButton={false}
                title="Campaign variable mappings"
                description="Fully editable — same options as template create (context field, static text, or deep link). Changes here apply only to this campaign; they do not overwrite the template."
                headerActions={
                  <button
                    type="button"
                    onClick={resetMappingsToTemplateDefaults}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium hover:bg-white dark:border-gray-600 dark:hover:bg-gray-800"
                  >
                    {templateDefaultMappings.length
                      ? 'Reset to template defaults'
                      : 'Reset empty mappings'}
                  </button>
                }
              />
            )}

            {selectedTemplate && isMediaHeaderFormat(selectedTemplate.headerFormat) && (
              <WhatsAppMediaHeaderUploader
                headerFormat={selectedTemplate.headerFormat}
                headerMediaUrl={form.headerMediaUrl || null}
                headerMimeType={form.headerMimeType || null}
                headerFileName={form.headerFileName || null}
                onChange={(media) =>
                  setForm((prev) => ({
                    ...prev,
                    headerMediaUrl: media.headerMediaUrl || '',
                    headerMimeType: media.headerMimeType || '',
                    headerFileName: media.headerFileName || '',
                  }))
                }
              />
            )}

            {form.campaignType === 'BROADCAST' && (
              <>
                <div>
                  <label className="mb-2 block text-xs font-medium text-gray-500">Audience *</label>
                  <div className="flex flex-wrap gap-2">
                    {(meta?.audiences || Object.keys(AUDIENCE_LABELS)).map((a) => {
                      const selected = form.targetAudience === a;
                      return (
                        <button
                          key={a}
                          type="button"
                          onClick={() => handleAudienceChange(a as WhatsAppTargetAudience)}
                          className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                            selected
                              ? 'border-[#25D366] bg-[#25D366] text-white'
                              : 'border-gray-200 bg-white text-gray-700 hover:border-[#25D366]/60 hover:bg-[#25D366]/5 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200'
                          }`}
                        >
                          {AUDIENCE_LABELS[a] || a}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {isPickerAudience(form.targetAudience) && (
                  <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                    <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {form.targetAudience === 'CUSTOM_PHONES' || allowMultiSelect
                            ? 'Selected recipients'
                            : `Select ${AUDIENCE_LABELS[form.targetAudience] || 'recipient'}`}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          Opens a side-by-side picker: filter list on the left, selected people on the right.
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {isSingleAudience(form.targetAudience) && !allowMultiSelect && (
                          <button
                            type="button"
                            onClick={enableMultiSelectMode}
                            className="rounded-lg border border-[#25D366] px-3 py-1.5 text-xs font-medium text-[#128C7E] hover:bg-[#25D366]/10"
                          >
                            Select multiple
                          </button>
                        )}
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => setRecipientModalOpen(true)}
                          className="rounded-lg bg-[#25D366] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#1ebe5d]"
                        >
                          {selectedRecipients.length ? 'Change selection' : 'Open recipient list'}
                        </button>
                      </div>
                    </div>

                    <div className="grid gap-3 md:grid-cols-[1.1fr_0.9fr]">
                      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-3 dark:border-gray-600 dark:bg-gray-900/40">
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                          How to pick
                        </p>
                        <ul className="mt-2 space-y-1 text-xs text-gray-600 dark:text-gray-300">
                          <li>1. Choose Users / Vendors / Contacts</li>
                          <li>2. Filter by platform, vendor type, or search</li>
                          <li>3. Multi-select or Select all shown</li>
                          <li>4. Confirm — selected list appears on the right</li>
                        </ul>
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
                        <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2 dark:border-gray-800">
                          <p className="text-xs font-semibold text-gray-800 dark:text-gray-100">
                            Selected ({selectedRecipients.length})
                          </p>
                          {selectedRecipients.length > 0 && (
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedRecipients([]);
                                setForm((prev) => ({
                                  ...prev,
                                  customPhonesText: '',
                                  targetDisplayName: '',
                                  targetUserId: '',
                                  targetVendorId: '',
                                  targetPhone: '',
                                }));
                              }}
                              className="text-[11px] text-red-600 hover:underline"
                            >
                              Clear
                            </button>
                          )}
                        </div>
                        <div className="max-h-40 overflow-y-auto">
                          {selectedRecipients.length === 0 ? (
                            <p className="px-3 py-6 text-center text-xs text-gray-500">
                              No one selected yet. Open the recipient list.
                            </p>
                          ) : (
                            selectedRecipients.map((r) => (
                              <div
                                key={r.key}
                                className="border-b border-gray-50 px-3 py-2 last:border-0 dark:border-gray-800"
                              >
                                <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                                  {r.displayName}
                                </p>
                                <p className="text-[11px] text-gray-500">
                                  {formatDisplayPhone(r.countryCode, r.phoneNumber)}
                                  {r.city ? ` · ${r.city}` : ''}
                                </p>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>

                    {(allowMultiSelect || form.targetAudience === 'CUSTOM_PHONES') && (
                      <div className="mt-4">
                        <label className="mb-1 block text-xs font-medium text-gray-500">
                          Or paste phone numbers (one per line / comma-separated)
                        </label>
                        <textarea
                          rows={3}
                          className={inputClass}
                          value={form.customPhonesText}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              targetAudience: 'CUSTOM_PHONES',
                              recipientMode: 'custom',
                              customPhonesText: e.target.value,
                            })
                          }
                          placeholder={'+919876543210\n9876543211'}
                        />
                      </div>
                    )}
                  </div>
                )}

                {!isPickerAudience(form.targetAudience) && (
                  <div className="space-y-3">
                    <div className="rounded-xl border border-dashed border-[#25D366]/40 bg-[#25D366]/5 px-4 py-3">
                      <p className="text-sm font-medium text-[#128C7E]">
                        Send only to selected filtered people?
                      </p>
                      <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                        Instead of the whole audience, open the picker and multi-select users, vendors, or
                        contacts (with Select all on the filtered list).
                      </p>
                      <button
                        type="button"
                        onClick={openFilteredSubsetPicker}
                        className="mt-2 rounded-lg bg-[#25D366] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#1ebe5d]"
                      >
                        Pick filtered users / vendors
                      </button>
                    </div>
                  </div>
                )}

                {form.campaignType === 'BROADCAST' && form.messageCategory === 'MARKETING' && (
                  <WhatsAppCampaignPolicySection
                    form={form}
                    setForm={setForm}
                    audienceCount={estimatedCount ?? previewTotal ?? 0}
                    templates={templates}
                  />
                )}

                <div className="flex flex-wrap gap-4 text-sm">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={form.scheduleMode === 'now'}
                      onChange={() => setForm({ ...form, scheduleMode: 'now' })}
                    />
                    Send now
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={form.scheduleMode === 'later'}
                      onChange={() => setForm({ ...form, scheduleMode: 'later' })}
                    />
                    Schedule
                  </label>
                </div>
                {form.scheduleMode === 'later' && (
                  <input
                    type="datetime-local"
                    value={form.scheduledAt}
                    onChange={(e) => setForm({ ...form, scheduledAt: e.target.value })}
                    className={inputClass}
                  />
                )}
                <div className="flex flex-wrap gap-3 text-xs">
                  <button
                    type="button"
                    onClick={() => void handleEstimate()}
                    className="text-gray-600 underline"
                  >
                    Estimate audience{estimatedCount != null ? `: ~${estimatedCount}` : ''}
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleOpenPreview()}
                    className="font-medium text-[#128C7E] underline"
                  >
                    Preview participants
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {messagePreviewOpen && (
        <div className="fixed inset-0 z-[90] flex justify-end">
          <button
            type="button"
            aria-label="Close preview"
            className="absolute inset-0 bg-black/40"
            onClick={() => setMessagePreviewOpen(false)}
          />
          <aside className="relative z-10 flex h-full w-full max-w-[380px] flex-col overflow-y-auto border-l border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900">
            <div className="flex items-center justify-between border-b px-4 py-3 dark:border-gray-700">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Message preview</p>
              <button
                type="button"
                onClick={() => setMessagePreviewOpen(false)}
                className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="flex flex-1 flex-col items-center p-4">
              <WhatsAppDevicePreview
                message={devicePreviewProps.message}
                headerText={devicePreviewProps.headerText}
                footer={devicePreviewProps.footer}
                buttons={devicePreviewProps.buttons}
                headerMediaUrl={devicePreviewProps.headerMediaUrl}
                headerMimeType={devicePreviewProps.headerMimeType}
                contactName={form.targetDisplayName || 'Vedika.Health'}
              />
              {form.sendPolicy.includeOptOut !== false && form.messageCategory === 'MARKETING' && (
                <p className="mt-3 text-center text-[11px] text-gray-500">
                  STOP opt-out is shown in this preview. The approved Meta template must include it.
                </p>
              )}
            </div>
          </aside>
        </div>
      )}

      {previewOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl dark:bg-gray-900">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Campaign preview</h3>
                <p className="text-sm text-gray-500">
                  {previewLoading
                    ? 'Loading participants…'
                    : `${previewTotal} participant${previewTotal === 1 ? '' : 's'} · review before send`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPreviewOpen(false)}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm dark:border-gray-600"
              >
                Close
              </button>
            </div>
            <div className="grid min-h-0 flex-1 gap-4 overflow-y-auto p-5 lg:grid-cols-2">
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Participants
                </p>
                {previewLoading ? (
                  <p className="text-sm text-gray-500">Loading…</p>
                ) : previewParticipants.length === 0 ? (
                  <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                    No participants matched this audience. Check single-user/vendor selection or filters.
                  </p>
                ) : (
                  <ul className="max-h-80 space-y-2 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700">
                    {previewParticipants.map((p, idx) => (
                      <li
                        key={`${p.recipientId || p.phone || idx}`}
                        className="border-b border-gray-100 px-3 py-2 text-sm last:border-0 dark:border-gray-800"
                      >
                        <p className="font-medium text-gray-900 dark:text-white">{p.name || 'Recipient'}</p>
                        <p className="text-xs text-gray-500">
                          {p.maskedPhone || p.phone || '—'} · {p.recipientType || p.source || 'contact'}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
                {previewTotal > previewParticipants.length ? (
                  <p className="mt-2 text-xs text-gray-500">
                    Showing first {previewParticipants.length} of {previewTotal}
                  </p>
                ) : null}
              </div>
              <div className="flex flex-col items-center">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Message mockup
                </p>
                <WhatsAppDevicePreview
                  message={devicePreviewProps.message}
                  headerText={devicePreviewProps.headerText}
                  footer={devicePreviewProps.footer}
                  buttons={devicePreviewProps.buttons}
                  headerMediaUrl={devicePreviewProps.headerMediaUrl}
                  headerMimeType={devicePreviewProps.headerMimeType}
                  contactName={
                    previewParticipants[0]?.name || form.targetDisplayName || 'Vedika.Health'
                  }
                />
              </div>
            </div>
            <div className="flex flex-wrap justify-end gap-2 border-t border-gray-200 px-5 py-4 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setPreviewOpen(false)}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm dark:border-gray-600"
              >
                Keep editing
              </button>
              <button
                type="button"
                disabled={saving || previewTotal === 0}
                onClick={() => {
                  setPreviewOpen(false);
                  void handleSave(true);
                }}
                className="inline-flex items-center gap-2 rounded-lg bg-[#25D366] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              >
                <PaperAirplaneIcon className="h-4 w-4" />
                Looks good — {form.scheduleMode === 'later' ? 'save & schedule' : 'save & send'}
              </button>
            </div>
          </div>
        </div>
      )}

      <WhatsAppRecipientSelectModal
        open={recipientModalOpen}
        onClose={() => setRecipientModalOpen(false)}
        onConfirm={handleRecipientModalConfirm}
        initialRecipients={selectedRecipients}
        singleSelect={!allowMultiSelect && form.targetAudience !== 'CUSTOM_PHONES'}
        allowedSources={
          form.targetAudience === 'CUSTOM_PHONES' || allowMultiSelect
            ? ['contacts', 'users', 'vendors']
            : allowedSourcesForAudience(form.targetAudience)
        }
        title={
          allowMultiSelect || form.targetAudience === 'CUSTOM_PHONES'
            ? 'Select filtered recipients'
            : `Select ${AUDIENCE_LABELS[form.targetAudience] || 'recipient'}`
        }
      />

      <WhatsAppCampaignSendPreflightDialog
        open={preflightOpen}
        loading={preflightLoading}
        preflight={preflightData}
        onCancel={() => setPreflightOpen(false)}
        onConfirm={(choice) => void confirmPreflightSend(choice)}
      />

    </div>
  );
};

export default WhatsAppCampaignEditorPage;
